import { useState, useRef, useEffect } from 'react'
import { useTenant } from '../contexts/TenantContext'
import { Building2, ChevronDown, Check, Search, Plus, Settings, Crown } from 'lucide-react'
import clsx from 'clsx'

export default function TenantSwitcher() {
  const { tenants, currentTenant, isMasterContext, switchTenant, isLoading } = useTenant()
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const dropdownRef = useRef(null)

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const filteredTenants = tenants.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    t.slug.toLowerCase().includes(search.toLowerCase())
  )

  if (isLoading) {
    return (
      <div className="h-9 w-40 bg-gray-100 rounded-lg animate-pulse" />
    )
  }

  if (!currentTenant) {
    return null
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          'flex items-center gap-2 px-3 py-2 rounded-lg transition-colors',
          'hover:bg-gray-100 border',
          isMasterContext ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-white'
        )}
      >
        <div className="w-6 h-6 rounded-md flex items-center justify-center text-white text-xs font-bold"
          style={{ backgroundColor: currentTenant.primary_color || '#69379C' }}>
          {currentTenant.display_icon || currentTenant.name.charAt(0).toUpperCase()}
        </div>
        <span className="font-medium text-sm max-w-[120px] truncate">{currentTenant.name}</span>
        {currentTenant.is_master && (
          <Crown className="w-3.5 h-3.5 text-amber-500" />
        )}
        <ChevronDown className={clsx('w-4 h-4 text-gray-400 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-white rounded-lg shadow-xl border border-gray-200 z-50 py-2">
          <div className="px-3 pb-2 border-b">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search tenants..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm border rounded-md"
                autoFocus
              />
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto py-1">
            {filteredTenants.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-gray-500">
                No tenants found
              </div>
            ) : (
              filteredTenants.map(tenant => (
                <button
                  key={tenant.id}
                  onClick={() => {
                    switchTenant(tenant.id)
                    setIsOpen(false)
                    setSearch('')
                  }}
                  className={clsx(
                    'w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 text-left',
                    tenant.id === currentTenant.id && 'bg-primary-50'
                  )}
                >
                  <div className="w-8 h-8 rounded-md flex items-center justify-center text-white text-sm font-bold shrink-0"
                    style={{ backgroundColor: tenant.primary_color || '#69379C' }}>
                    {tenant.display_icon || tenant.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-sm truncate">{tenant.name}</span>
                      {tenant.is_master && (
                        <Crown className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                      )}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {tenant.slug} • {tenant.contacts_count || 0} contacts
                    </div>
                  </div>
                  {tenant.id === currentTenant.id && (
                    <Check className="w-4 h-4 text-primary-600 shrink-0" />
                  )}
                </button>
              ))
            )}
          </div>

          {isMasterContext && (
            <>
              <div className="border-t my-1" />
              <a
                href="/tenants"
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
                onClick={() => setIsOpen(false)}
              >
                <Settings className="w-4 h-4" />
                Manage Tenants
              </a>
            </>
          )}
        </div>
      )}
    </div>
  )
}
